'use client';

import * as React from 'react';
import type { SidebarSection, SearchResult } from './types';

export interface ISidebarContext {
  sidebarOpen: boolean;
  setSidebarState: (state: boolean) => void;
  toggleSidebar: () => void;
  searchValue: string;
  setSearchValue: (state: string) => void;
  searchResults: SearchResult[] | null;
  setSearchResults: (state: SearchResult[] | null) => void;
  filteredSections: SidebarSection[] | null;
  setFilteredSections: (state: SidebarSection[]) => void;
  sectionsHashKey: string;
  setSectionsHashKey: (state: string) => void;
  expandedItems: string[];
  setExpandedItems: (items: string[]) => void;
  scrollPosition: number;
  setScrollPosition: (scrollPosition: number) => void;
}

export const SidebarContext = React.createContext<ISidebarContext>({
  sidebarOpen: false,
  setSidebarState: () => {},
  toggleSidebar: () => {},
  searchResults: null,
  setSearchResults: () => {},
  filteredSections: null,
  setFilteredSections: () => {},
  searchValue: '',
  setSearchValue: () => {},
  sectionsHashKey: '',
  setSectionsHashKey: () => {},
  expandedItems: [],
  setExpandedItems: () => {},
  scrollPosition: 0,
  setScrollPosition: () => {},
});

export const SidebarProvider = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<SearchResult[] | null>(null);
  const [filteredSections, setFilteredSections] = React.useState<SidebarSection[] | null>(null);
  const [sectionsHashKey, setSectionsHashKey] = React.useState<string>('');
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const [scrollPosition, setScrollPosition] = React.useState<number>(0);

  const setSidebarState = (state: boolean) => {
    setIsOpen(state);
  };

  const toggleSidebar = React.useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const contextValue = React.useMemo(
    () => ({
      sidebarOpen: isOpen,
      setSidebarState,
      toggleSidebar,
      searchResults,
      setSearchResults,
      filteredSections,
      setFilteredSections,
      searchValue,
      setSearchValue,
      sectionsHashKey,
      setSectionsHashKey,
      expandedItems,
      setExpandedItems,
      scrollPosition,
      setScrollPosition,
    }),
    [
      isOpen,
      scrollPosition,
      toggleSidebar,
      searchResults,
      filteredSections,
      searchValue,
      sectionsHashKey,
      expandedItems,
    ],
  );

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
};
